import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHead, Progress } from "../components/ui-bits";
import { Link } from "react-router-dom";
import { FileText, Briefcase, Warning, Lightbulb, FolderOpen, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { SERVICE_META } from "../lib/services";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [scores, setScores] = useState(null);
  const [recs, setRecs] = useState([]);
  const [biz, setBiz] = useState(null);

  useEffect(() => {
    api.get("/dashboard/services").then((r) => setData(r.data));
    api.get("/scores").then((r) => setScores(r.data));
    api.get("/recommendations").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/business/profile").then((r) => setBiz(r.data)).catch(() => {});
  }, []);

  if (!data || !scores) return <p className="label">Loading dashboard…</p>;
  const { services, summary } = data;

  const kpis = [
    { label: "Services", value: summary.services_count, Icon: FolderOpen },
    { label: "Active Tasks", value: summary.active_tasks, Icon: Briefcase },
    { label: "Completed", value: summary.completed_tasks, Icon: CheckCircle },
    { label: "Docs Pending", value: summary.pending_docs, Icon: FileText, warn: summary.pending_docs > 0 },
    { label: "Overdue Filings", value: summary.overdue, Icon: Warning, warn: summary.overdue > 0 },
  ];

  return (
    <div>
      <PageHead eyebrow="Your Services at a Glance" title="Dashboard" />

      {biz && (
        <div data-testid="business-banner" style={{ background: "#111111" }} className="p-5 mb-6 rounded-sm text-white flex flex-wrap items-center gap-x-10 gap-y-3">
          <div><p className="label text-white/40 mb-0.5">Business</p><p className="font-head font-bold text-lg text-white">{biz.company}</p></div>
          {biz.business?.business_type && <div><p className="label text-white/40 mb-0.5">Type</p><p className="text-sm text-white">{biz.business.business_type}</p></div>}
          {biz.business?.gstin && <div><p className="label text-white/40 mb-0.5">GSTIN</p><p className="text-sm tnum text-white">{biz.business.gstin}</p></div>}
          {biz.business?.city && <div><p className="label text-white/40 mb-0.5">City</p><p className="text-sm text-white">{biz.business.city}</p></div>}
          <div><p className="label text-white/40 mb-0.5">Plan</p><p className="text-sm text-white">{biz.plan || "Starter"}</p></div>
          {biz.assigned_agent_name && <div className="ml-auto"><p className="label text-white/40 mb-0.5">Your CFO Agent</p><p className="text-sm text-[#00A86B] font-semibold">{biz.assigned_agent_name}</p></div>}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {kpis.map((k, i) => (
          <div key={i} data-testid={`dash-kpi-${i}`} className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="label">{k.label}</p>
              <k.Icon size={18} className={k.warn ? "text-[#FF3B30]" : "text-[#002FA7]"} />
            </div>
            <p className={`font-head font-black text-3xl tnum ${k.warn ? "text-[#FF3B30]" : ""}`}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="label">Your Services</p>
        <Link to="/projects" data-testid="dash-view-tracker" className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">Open Project Tracker <ArrowRight size={12} /></Link>
      </div>

      {services.length === 0 ? (
        <div className="card p-8 text-center mb-6">
          <FolderOpen size={32} className="mx-auto mb-2 text-[#bbb]" />
          <p className="text-sm text-[#888]">No services assigned yet. Your CFO team will set these up shortly.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {services.map((s, i) => {
            const meta = SERVICE_META[s.service] || SERVICE_META.Other;
            return (
              <Link to={`/projects?service=${encodeURIComponent(s.service)}`} key={s.service} data-testid={`service-card-${i}`}
                className="card p-5 group hover:-translate-y-0.5 hover:border-[color:var(--c)] transition-all" style={{ "--c": meta.color }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-sm flex items-center justify-center" style={{ background: `${meta.color}15` }}>
                    <meta.Icon size={22} style={{ color: meta.color }} weight="fill" />
                  </div>
                  <div>
                    <p className="font-head font-bold">{s.service}</p>
                    <p className="text-xs text-[#888]">{meta.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Progress value={s.progress} />
                  <span className="tnum text-xs font-semibold w-9 text-right">{s.progress}%</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#555]">Stage: <b className="text-[#111]">{s.stage}</b></span>
                  <span className="text-[#888]">{s.completed}/{s.total} done</span>
                </div>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-black/10 text-xs">
                  {s.pending_docs > 0
                    ? <span className="text-[#8a6d00] flex items-center gap-1"><FileText size={13} /> {s.pending_docs} doc(s) needed</span>
                    : <span className="text-[#00A86B] flex items-center gap-1"><CheckCircle size={13} /> Docs complete</span>}
                  {s.next_deadline && <span className="ml-auto text-[#FF3B30] tnum">Due {s.next_deadline.date}</span>}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card p-6 lg:col-span-1 bg-[#111111] text-white">
          <p className="label text-white/50 mb-2">Business Health Score</p>
          <div className="flex items-end gap-2 mb-6">
            <span className="font-head font-black text-6xl tnum">{scores.health.overall}</span>
            <span className="text-white/40 mb-2">/100</span>
          </div>
          {Object.entries(scores.health).filter(([k]) => k !== "overall").map(([k, v]) => (
            <div key={k} className="mb-3">
              <div className="flex justify-between text-xs mb-1"><span className="text-white/60">{k}</span><span className="tnum">{v}</span></div>
              <div className="h-1.5 bg-white/10"><div style={{ width: `${v}%` }} className="h-full bg-[#00A86B]" /></div>
            </div>
          ))}
        </div>

        <div className="card p-6">
          <p className="label mb-4 flex items-center gap-2"><Warning size={16} color="#FF3B30" /> Upcoming Deadlines</p>
          <div className="space-y-3">
            {(summary.next_deadlines || []).map((d, i) => (
              <div key={i} data-testid={`dash-deadline-${i}`} className="flex justify-between items-center text-sm">
                <span>{d.name}</span>
                <span className={`tnum ${d.status === "overdue" ? "text-[#FF3B30] font-semibold" : "text-[#555]"}`}>{d.date}</span>
              </div>
            ))}
            {(summary.next_deadlines || []).length === 0 && <p className="text-sm text-[#888]">Nothing due — you're all caught up 🎉</p>}
          </div>
          <Link to="/compliance" className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-4">View compliance calendar <ArrowRight size={12} /></Link>
        </div>

        <div className="card p-6">
          <p className="label mb-4 flex items-center gap-2"><Lightbulb size={16} weight="fill" color="#002FA7" /> CFO Recommendations</p>
          <div className="space-y-3">
            {recs.slice(0, 4).map((r, i) => (
              <div key={i} className="flex items-start gap-2 border-l-2 pl-3 text-sm" style={{ borderColor: r.impact === "high" ? "#FF3B30" : "#FFD700" }}>
                {r.text}
              </div>
            ))}
            {recs.length === 0 && <p className="text-sm text-[#888]">No recommendations yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
